import type { Tables } from '../../types/database.types';
import type { CgOrder, CgOrderItem } from './types';
import { orderTotal } from './types';

/**
 * What an order made, once the supplier has billed us.
 *
 * Kyu, 22 September: the order amount and the S&H fee go in when the order
 * does; the supplier bills about two weeks later, for bracelets, packaging,
 * handling, not always itemised the way our lines are. So the bill is free
 * lines (cg_order_costs), and:
 *
 *   Revenue = order lines + S&H
 *   Bill    = the bill's lines added up
 *   Profit  = Revenue - Bill
 *   Margin  = Profit / Revenue
 *
 * Until there is a bill, there is no profit. Not zero, not "revenue minus
 * whatever costs happen to be typed": the old lens summed only the lines that
 * had a cost, so a three-line order with one cost entered showed nearly all
 * of its revenue as profit.
 */

export type CgOrderCost = Tables<'cg_order_costs'>;

/** Where the bill figure came from. 'legacy' is an order costed before bills existed. */
export type BillSource = 'bill' | 'legacy' | 'pending';

export interface OrderProfit {
  revenue: number;
  bill: number | null;
  profit: number | null;
  marginPct: number | null;
  source: BillSource;
}

function cents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The cost an order carried before supplier bills: the drawer's total override,
 * or every line's unit cost. A partial set of unit costs is not a cost.
 */
function legacyCost(order: CgOrder, items: CgOrderItem[]): number | null {
  if (order.cost_override_usd !== null) {
    return Number(order.cost_override_usd);
  }
  if (items.length === 0 || items.some((item) => item.unit_cost_usd === null)) {
    return null;
  }
  return items.reduce((sum, item) => sum + item.units * Number(item.unit_cost_usd), 0);
}

export function billTotal(costs: readonly CgOrderCost[]): number {
  return cents(costs.reduce((sum, line) => sum + Number(line.amount_usd), 0));
}

export function orderProfit(
  order: CgOrder,
  items: CgOrderItem[],
  costs: readonly CgOrderCost[],
): OrderProfit {
  const revenue = cents(orderTotal(order, items));
  let bill: number | null;
  let source: BillSource;
  if (costs.length > 0) {
    bill = billTotal(costs);
    source = 'bill';
  } else {
    const legacy = legacyCost(order, items);
    bill = legacy === null ? null : cents(legacy);
    source = legacy === null ? 'pending' : 'legacy';
  }
  if (bill === null) {
    return { revenue, bill: null, profit: null, marginPct: null, source };
  }
  const profit = cents(revenue - bill);
  return {
    revenue,
    bill,
    profit,
    marginPct: revenue > 0 ? (profit / revenue) * 100 : null,
    source,
  };
}

/** Group bill lines by order, each list in the order it was typed. */
export function costsByOrder(costs: readonly CgOrderCost[]): Map<string, CgOrderCost[]> {
  const map = new Map<string, CgOrderCost[]>();
  for (const line of costs) {
    const list = map.get(line.order_id);
    if (list === undefined) {
      map.set(line.order_id, [line]);
    } else {
      list.push(line);
    }
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.position - b.position);
  }
  return map;
}

/**
 * Names typed on earlier bills, most used first, for the name field to offer.
 * Matched without regard to case or spacing, and shown the way it was most
 * often typed, so "packaging " and "Packaging" are one suggestion.
 */
export function costNameSuggestions(costs: readonly CgOrderCost[]): string[] {
  const groups = new Map<string, { count: number; spellings: Map<string, number> }>();
  for (const line of costs) {
    const shown = line.label.trim().replace(/\s+/g, ' ');
    if (shown === '') {
      continue;
    }
    const key = shown.toLowerCase();
    const group = groups.get(key) ?? { count: 0, spellings: new Map<string, number>() };
    group.count += 1;
    group.spellings.set(shown, (group.spellings.get(shown) ?? 0) + 1);
    groups.set(key, group);
  }
  return [...groups.values()]
    .map((group) => {
      const [best] = [...group.spellings.entries()].sort((a, b) => b[1] - a[1]);
      return { name: best?.[0] ?? '', count: group.count };
    })
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .map((entry) => entry.name);
}

/** A bill line as the editor holds it: text, so a half-typed amount is not lost. */
export interface BillDraftLine {
  label: string;
  amount: string;
}

export interface BillLineInput {
  label: string;
  amount_usd: number;
}

/**
 * Turn the editor's rows into what set_order_costs takes, or say why not.
 * Fully empty rows are dropped, so the spare row at the bottom never blocks a
 * save. A row with only one half filled is an error, not a silent drop.
 */
export function parseBillDraft(
  lines: readonly BillDraftLine[],
): { ok: true; lines: BillLineInput[] } | { ok: false; error: string } {
  const out: BillLineInput[] = [];
  for (const [index, line] of lines.entries()) {
    const label = line.label.trim().replace(/\s+/g, ' ');
    const raw = line.amount.replace(/[$,\s]/g, '');
    if (label === '' && raw === '') {
      continue;
    }
    const row = index + 1;
    if (label === '') {
      return { ok: false, error: `Line ${row} needs a name.` };
    }
    if (label.length > 80) {
      return { ok: false, error: `Line ${row}: keep the name under 80 characters.` };
    }
    if (raw === '') {
      return { ok: false, error: `Line ${row} needs an amount.` };
    }
    const amount = Number(raw);
    if (!Number.isFinite(amount) || !/^\d+(\.\d{0,2})?$/.test(raw)) {
      return { ok: false, error: `Line ${row}: "${line.amount}" is not a dollar amount.` };
    }
    out.push({ label, amount_usd: cents(amount) });
  }
  return { ok: true, lines: out };
}

/** One order's row in the Reports profit section. */
export interface ProfitRow {
  order: CgOrder;
  result: OrderProfit;
  lines: CgOrderCost[];
}

/** One cost name added up across a period's bills. */
export interface CostRollup {
  name: string;
  total: number;
}

/**
 * The name for bills with no lines: orders costed in the drawer before
 * 20270162000100. Shown so the roll-up adds up to the bill total rather than
 * quietly falling short of it.
 */
export const OLDER_COST_LABEL = 'Costed before bills (no breakdown)';

export interface ProfitSummary {
  /** Revenue of the billed orders only, so margin compares like with like. */
  revenue: number;
  bills: number;
  profit: number;
  /** Profit per dollar of revenue across the billed orders, as a percentage. */
  marginPct: number | null;
  billedCount: number;
  pendingCount: number;
  avgProfit: number | null;
  rows: ProfitRow[];
  rollup: CostRollup[];
}

/**
 * The Reports profit section, over whatever orders the report's filters let
 * through (drop 241).
 *
 * Only billed orders count toward the totals. An order still waiting on its
 * bill has revenue and no cost; adding its revenue in would make the margin
 * look better the more bills are outstanding, which is backwards. It is
 * counted separately instead.
 *
 * Margin is total profit over total revenue, not the average of each order's
 * margin: a $300 order at 60% should not weigh the same as a $30,000 order at
 * 35%. That is what "average margin per dollar" means.
 */
export function profitSummary(
  orders: readonly CgOrder[],
  itemsByOrder: ReadonlyMap<string, CgOrderItem[]>,
  billsByOrder: ReadonlyMap<string, CgOrderCost[]>,
): ProfitSummary {
  const rows: ProfitRow[] = orders.map((order) => {
    const lines = billsByOrder.get(order.id) ?? [];
    return { order, lines, result: orderProfit(order, itemsByOrder.get(order.id) ?? [], lines) };
  });
  let revenue = 0;
  let bills = 0;
  let billedCount = 0;
  const byName = new Map<string, CostRollup>();
  for (const row of rows) {
    if (row.result.bill === null) {
      continue;
    }
    billedCount += 1;
    revenue += row.result.revenue;
    bills += row.result.bill;
    if (row.lines.length === 0) {
      const older = byName.get('') ?? { name: OLDER_COST_LABEL, total: 0 };
      older.total += row.result.bill;
      byName.set('', older);
      continue;
    }
    for (const line of row.lines) {
      const shown = line.label.trim().replace(/\s+/g, ' ');
      const key = shown.toLowerCase();
      const entry = byName.get(key) ?? { name: shown, total: 0 };
      entry.total += Number(line.amount_usd);
      byName.set(key, entry);
    }
  }
  revenue = cents(revenue);
  bills = cents(bills);
  const profit = cents(revenue - bills);
  return {
    revenue,
    bills,
    profit,
    marginPct: billedCount > 0 && revenue > 0 ? (profit / revenue) * 100 : null,
    billedCount,
    pendingCount: rows.length - billedCount,
    avgProfit: billedCount > 0 ? cents(profit / billedCount) : null,
    rows,
    rollup: [...byName.values()]
      .map((entry) => ({ ...entry, total: cents(entry.total) }))
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name)),
  };
}
